import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { IndianRupee, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

export default function CostFunding() {
  const [schemes, setSchemes] = useState([]);
  const [check, setCheck] = useState({ age: "", income_group: "low", has_disability_certificate: false, state: "", diagnosis: "" });
  const [matched, setMatched] = useState(null);

  useEffect(() => { api.get("/funding-schemes").then(({ data }) => setSchemes(data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...check, age: parseInt(check.age, 10) };
    try {
      const { data } = await api.post("/funding-check", payload);
      setMatched(data);
    } catch (err) {}
  };

  return (
    <>
      <SEO
        title="Cochlear Implant Cost in India & Funding Options"
        description="Cochlear implant cost in India explained — implant price, surgery cost, hospital charges, mapping & therapy. Government schemes (ADIP, CMCHIS, SAST, Aarogyasri, Sruthitharangam), PMJAY, CSR, NGO and EMI options."
      />
      <section className="bg-gradient-to-br from-amber-50 via-white to-[#ECFDF5] py-14">
        <div className="hc-container max-w-4xl">
          <div className="text-amber-700 uppercase tracking-wider text-[12px] font-bold mb-3">Cost & funding</div>
          <h1 className="font-serif text-[2.25rem] md:text-[3rem] font-semibold leading-[1.08] text-slate-900">
            Cochlear Implant Cost in India
          </h1>
          <p className="mt-5 text-slate-700 text-[1.08rem]">
            CI total cost in India typically ranges <strong>₹5.5 L – ₹14 L</strong>. Government schemes, CSR and NGO support
            can significantly reduce out-of-pocket cost for eligible families.
          </p>
        </div>
      </section>

      <section className="hc-container py-14">
        <h2 className="font-serif text-2xl font-semibold text-slate-900">Cost breakdown (typical)</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ["Implant device", "₹4.5L – ₹9L", "Varies by manufacturer and model"],
            ["Surgery & hospital", "₹1L – ₹3L", "Varies by hospital tier"],
            ["Anaesthesia & investigations", "₹30k – ₹60k", "Pre-op imaging, blood work"],
            ["Mapping (1st year)", "₹15k – ₹40k", "Multiple sessions in first year"],
            ["AVT / speech therapy", "₹40k – ₹1L+", "12–24 months structured therapy"],
            ["Batteries & accessories", "₹5k – ₹15k/year", "Ongoing"],
          ].map(([t, p, d]) => (
            <div key={t} className="card-hc">
              <div className="text-[12px] uppercase font-bold text-amber-700 tracking-wider">{t}</div>
              <div className="font-serif text-2xl font-semibold text-slate-900 mt-1.5 flex items-center gap-1"><IndianRupee size={20} className="text-slate-400" />{p}</div>
              <p className="mt-1 text-slate-600 text-[14px]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 hc-section">
        <div className="hc-container">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-slate-900">Government schemes & funding</h2>
          <p className="mt-3 text-slate-700 max-w-2xl">Several central and state schemes support cochlear implant surgery for eligible families. Eligibility criteria vary.</p>

          <div className="mt-8 grid lg:grid-cols-2 gap-5">
            {schemes.map((s) => (
              <div key={s.id || s.name} data-testid={`scheme-${(s.short_name || s.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="text-[11px] uppercase tracking-wider font-bold text-[#0F766E]">{s.type}</div>
                <div className="font-serif text-xl font-semibold text-slate-900 mt-1">{s.name}</div>
                <p className="mt-3 text-slate-700 text-[15px]">{s.summary}</p>
                {s.eligibility?.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {s.eligibility.slice(0, 4).map((e, i) => <li key={i} className="text-[14px] text-slate-700 flex gap-2"><CheckCircle2 size={15} className="text-[#0F766E] mt-1 shrink-0" />{e}</li>)}
                  </ul>
                )}
                {s.website && <a target="_blank" rel="noopener noreferrer" href={s.website} className="mt-4 inline-flex items-center gap-1 text-[#0369A1] font-semibold text-sm">Official link <ArrowRight size={14} /></a>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Funding checker */}
      <section className="hc-container py-14">
        <div className="bg-gradient-to-br from-[#0369A1] to-[#0F766E] text-white rounded-3xl p-8 md:p-12">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold">Funding Eligibility Checker</h2>
          <p className="mt-3 text-sky-50 max-w-2xl">Tell us a few details. We'll suggest schemes you may qualify for. This is indicative — final eligibility is decided by the scheme authority.</p>

          <form data-testid="funding-check-form" onSubmit={submit} className="mt-7 grid md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-[13px] font-medium text-sky-100 mb-1">Patient age</label>
              <input data-testid="fcheck-age" required type="number" min={0} className="input-hc text-slate-900" value={check.age} onChange={(e) => setCheck({ ...check, age: e.target.value })} />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-sky-100 mb-1">Income group</label>
              <select data-testid="fcheck-income" className="input-hc text-slate-900" value={check.income_group} onChange={(e) => setCheck({ ...check, income_group: e.target.value })}>
                <option value="bpl">BPL</option>
                <option value="low">Low income</option>
                <option value="middle">Middle income</option>
                <option value="high">High income</option>
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-sky-100 mb-1">State</label>
              <input data-testid="fcheck-state" required className="input-hc text-slate-900" value={check.state} onChange={(e) => setCheck({ ...check, state: e.target.value })} placeholder="e.g. Tamil Nadu" />
            </div>
            <div>
              <label className="flex items-center gap-2 text-[14px] text-sky-50 cursor-pointer mt-7">
                <input data-testid="fcheck-dc" type="checkbox" className="w-5 h-5 accent-amber-400" checked={check.has_disability_certificate} onChange={(e) => setCheck({ ...check, has_disability_certificate: e.target.checked })} />
                Have disability certificate
              </label>
            </div>
            <button data-testid="fcheck-submit" type="submit" className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold rounded-xl px-6 py-3.5">Check schemes</button>
          </form>

          {matched && (
            <div className="mt-7 bg-white text-slate-900 rounded-2xl p-6">
              <div className="text-[13px] uppercase tracking-wider font-bold text-[#0F766E]">Result</div>
              <div className="font-serif text-xl font-semibold mt-1">{matched.matched_count} scheme{matched.matched_count !== 1 && "s"} may apply</div>
              {matched.schemes?.length > 0 ? (
                <ul className="mt-4 space-y-2">
                  {matched.schemes.map((s, i) => <li key={i} className="flex gap-2 text-[15px]"><CheckCircle2 size={18} className="text-[#0F766E] mt-1 shrink-0" /> <strong>{s.name}</strong></li>)}
                </ul>
              ) : <p className="mt-2 text-slate-600">No direct match. Speak to a HearClear counsellor for personalised guidance.</p>}
              <p className="mt-4 text-[13px] text-slate-500 flex gap-2"><AlertCircle size={14} className="mt-0.5" /> {matched.disclaimer}</p>
              <Link to="/register" className="btn-primary mt-5">Talk to a funding counsellor</Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
